import { FlashcardCategory, type FlashcardSessionSize } from "@/lib/constant";
import type { FlashcardWord } from "@/types/common";

export type FlashcardSwipeResult = { word: FlashcardWord; category: FlashcardCategory };

export type HeaderProgress = { numerator: number; denominator: number };

export function splitFlashcardSession(words: FlashcardWord[], sessionSize: FlashcardSessionSize) {
  if (sessionSize === "all") {
    return { sessionWords: words, remainingWords: [] };
  }

  return {
    sessionWords: words.slice(0, sessionSize),
    remainingWords: words.slice(sessionSize),
  };
}

export function summarizeSessionHistory(history: FlashcardSwipeResult[]) {
  let sessionKnownCount = 0;
  let sessionLearningCount = 0;
  const learningWords: FlashcardWord[] = [];

  for (const r of history) {
    if (r.category === FlashcardCategory.LEARNING) {
      sessionLearningCount += 1;
      learningWords.push(r.word);
    } else {
      sessionKnownCount += 1;
    }
  }

  return { sessionKnownCount, sessionLearningCount, learningWords };
}

/** 헤더 n/m — 학습 덱(분류 완료 후 큐) vs 미분류 세트 */
export function computeHeaderProgress({
  totalWordCount,
  deckSize,
  sessionClassifiedCount,
  baseKnownCount,
  baseLearningCount,
  learningDeckMode,
  chunkedSession = false,
}: {
  totalWordCount: number;
  deckSize: number;
  /** 이번 화면에서 스와이프로 분류 완료한 장 수 */
  sessionClassifiedCount: number;
  baseKnownCount: number;
  baseLearningCount: number;
  learningDeckMode: boolean;
  /** 세션 크기가 제한된 경우에는 전체 세트가 아니라 현재 세션 기준으로 표시 */
  chunkedSession?: boolean;
}): HeaderProgress {
  if (totalWordCount <= 0) {
    return { numerator: 0, denominator: 0 };
  }

  if (chunkedSession && deckSize > 0) {
    return {
      numerator: Math.min(sessionClassifiedCount + 1, deckSize),
      denominator: deckSize,
    };
  }

  // 분류 완료 후 학습 큐: 이번 라운드에서 분류한 개수(+ 현재 카드 1)
  if (learningDeckMode) {
    if (deckSize > 0) {
      return {
        numerator: Math.min(sessionClassifiedCount + 1, deckSize),
        denominator: deckSize,
      };
    }

    return { numerator: totalWordCount, denominator: totalWordCount };
  }

  // 미분류 남음: DB 알고 있음 + 학습 중 + 이번 세션 분류 + 1(지금 보는 카드)
  if (deckSize === 0) {
    return { numerator: totalWordCount, denominator: totalWordCount };
  }

  return {
    numerator: Math.min(baseKnownCount + baseLearningCount + sessionClassifiedCount + 1, totalWordCount),
    denominator: totalWordCount,
  };
}

/** CountBadge용 알고 있음 / 학습 중 */
export function computeBadgeCounts({
  learningDeckMode,
  baseKnownCount,
  baseLearningCount,
  totalWordCount,
  sessionKnownCount,
  sessionLearningCount,
}: {
  learningDeckMode: boolean;
  baseKnownCount: number;
  baseLearningCount: number;
  totalWordCount: number;
  sessionKnownCount: number;
  sessionLearningCount: number;
}): { known: number; learning: number } {
  const setFullyClassified = totalWordCount > 0 && baseKnownCount + baseLearningCount >= totalWordCount;

  // 세트 단어가 모두 known/learning으로 잡힌 뒤의 학습 큐만: 이번 라운드 스와이프만 0부터
  if (learningDeckMode && setFullyClassified) {
    return { known: sessionKnownCount, learning: sessionLearningCount };
  }

  return {
    known: baseKnownCount + sessionKnownCount,
    learning: Math.max(
      0,
      learningDeckMode || setFullyClassified
        ? baseLearningCount - sessionKnownCount
        : baseLearningCount + sessionLearningCount,
    ),
  };
}
